<template>
  <v-col
    cols="12"
    :md="md"
    :data-ai-field="fieldKey"
    :class="{
      'onboarding-target--ai-suggested': aiStore.isFieldAiSuggested(fieldKey),
    }"
  >
    <div class="onboarding-field-label-row">
      <div class="d-flex align-center ga-2">
        <label class="text-body-2 font-weight-medium">
          {{ label }}
        </label>
        <span class="onboarding-inline-badge-slot">
          <AiBadge
            v-if="aiCaptured"
            :label="$t('onboarding.wizard.data-entry.ai-captured')"
          />
        </span>
      </div>
    </div>
    <div class="onboarding-image-slot">
      <v-overlay
        :model-value="uploading"
        contained
        class="align-center justify-center rounded-lg"
        persistent
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="36"
        />
      </v-overlay>
      <div
        class="onboarding-image-slot__frame"
        :class="{
          'onboarding-image-slot__frame--empty': !displayUrl,
          'onboarding-image-slot__frame--clickable': canUpload,
        }"
        role="button"
        :tabindex="canUpload ? 0 : -1"
        @click="openPicker"
        @keydown.enter.prevent="openPicker"
        @keydown.space.prevent="openPicker"
      >
        <r-img
          v-if="displayUrl"
          :src="displayUrl"
          cover
          class="onboarding-image-slot__img"
        />
        <v-icon
          v-else
          size="40"
          color="grey"
        >
          mdi-plus
        </v-icon>
      </div>
      <CropImageUpload
        ref="cropRef"
        class="onboarding-image-slot__crop"
        :id="`onboarding-field-${fieldKey}`"
        :name="cropName"
        :disabled="!canUpload"
        :min-height="minHeight"
        :min-width="minWidth"
        :ratio="ratio"
        :save="onSave"
      />
    </div>
  </v-col>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import CropImageUpload from '@/components/CropImageUpload.vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'

import AiBadge from '../../AiBadge.vue'
import type { OnboardingImageKind } from '../../composables/useOnboardingFormImages'

const props = defineProps<{
  kind: OnboardingImageKind
  fieldKey: string
  label: string
  md: number
  cropName: string
  minHeight: number
  minWidth: number
  ratio: number
  displayUrl: string
  aiCaptured?: boolean
  uploading: boolean
  canUpload: boolean
  persistImage: (kind: OnboardingImageKind, blob: Blob) => Promise<void>
}>()

const aiStore = useAiAssistantStore()
const cropRef = ref<{ onUploadClick?: () => void } | null>(null)

function openPicker() {
  if (!props.canUpload) return
  cropRef.value?.onUploadClick?.()
}

function onSave(blob: Blob) {
  return props.persistImage(props.kind, blob)
}
</script>

<style scoped>
.onboarding-field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.onboarding-inline-badge-slot {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
}

.onboarding-target--ai-suggested {
  border-radius: 12px;
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.25);
}

.onboarding-image-slot {
  position: relative;
  max-width: 220px;
}

.onboarding-image-slot__frame {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), 0.24);
  margin-bottom: 8px;
  background: rgba(var(--v-theme-surface-variant), 0.2);
}

.onboarding-image-slot__frame--empty {
  display: flex;
  align-items: center;
  justify-content: center;
}

.onboarding-image-slot__frame--clickable {
  cursor: pointer;
}

.onboarding-image-slot__frame--clickable:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.onboarding-image-slot__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.onboarding-image-slot__crop :deep(.v-btn) {
  width: 100%;
}
</style>
